import { NavOutput, PageOutput, RedirectItem, SearchChunk } from '../types';
import { DEFAULT_SITE, type SiteId } from './sites';

let navCache: { token: string | null; site: SiteId; data: NavOutput } | null = null;
let redirectsCache: RedirectItem[] | null = null;
let chunksCache: { token: string | null; site: SiteId; data: SearchChunk[] } | null = null;
let siteConnections: Promise<Record<string, string>> | null = null;

export function getStoredAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('crisp_embed_token');
  } catch {
    return null;
  }
}

export class PageApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PageApiError';
    this.status = status;
  }
}

function authHeaders(token?: string | null): Record<string, string> {
  const effectiveToken = token || getStoredAuthToken();
  const headers: Record<string, string> = {};
  if (effectiveToken) {
    headers.Authorization = `Bearer ${effectiveToken}`;
  }
  return headers;
}

function encodeSlugPath(slug: string): string {
  return slug
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

async function readErrorDetail(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (typeof body?.message === 'string') return body.message;
  } catch {
    try {
      const text = await res.text();
      if (text) return text;
    } catch {
      // ignore
    }
  }
  return fallback;
}

/**
 * Fetches the navigation configuration via the authenticated API.
 */
export async function fetchNavigation(site: SiteId = DEFAULT_SITE.id, token?: string | null): Promise<NavOutput> {
  const effectiveToken = token || getStoredAuthToken();
  if (navCache && navCache.token === effectiveToken && navCache.site === site) return navCache.data;

  const res = await fetch(`/api/v1/nav?site=${site}`, { headers: authHeaders(effectiveToken) });
  if (!res.ok) {
    throw new Error(await readErrorDetail(res, 'Failed to load navigation configuration'));
  }

  const data = (await res.json()) as NavOutput;
  navCache = { token: effectiveToken, site, data };
  return data;
}

/**
 * The Auth0 connection each site's entry in docs.json names, by site id. A
 * failed fetch is forgotten so the next login tries again.
 */
export function fetchSiteConnections(): Promise<Record<string, string>> {
  siteConnections ??= fetch('/api/v1/sites')
    .then(async (res) => {
      if (!res.ok) throw new Error(await readErrorDetail(res, 'Failed to load site settings'));
      const sites = (await res.json()) as Array<{ id: string; connection: string | null }>;
      return Object.fromEntries(
        sites.flatMap((site) => (site.connection ? [[site.id, site.connection] as const] : []))
      );
    })
    .catch((err) => {
      siteConnections = null;
      throw err;
    });
  return siteConnections;
}

/**
 * Fetches a single page JSON by slug from the RBAC-protected API.
 */
export async function fetchPage(slug: string, site: SiteId = DEFAULT_SITE.id, token?: string | null): Promise<PageOutput> {
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, '') || 'index';
  const effectiveToken = token || getStoredAuthToken();
  const encodedSlug = encodeSlugPath(normalizedSlug);

  const res = await fetch(`/api/v1/pages/${encodedSlug}?site=${site}`, {
    headers: authHeaders(effectiveToken),
  });

  if (res.ok) {
    return (await res.json()) as PageOutput;
  }

  if (res.status === 401) {
    throw new PageApiError(401, 'Authentication required to view this page');
  }
  if (res.status === 403) {
    throw new PageApiError(403, 'You do not have access to this page');
  }
  if (res.status === 404) {
    throw new PageApiError(404, `Page not found: ${normalizedSlug}`);
  }

  throw new PageApiError(res.status, await readErrorDetail(res, `Failed to load page (${res.status})`));
}

/**
 * Fetches redirects configuration. Redirects are not access-controlled content.
 */
export async function fetchRedirects(): Promise<RedirectItem[]> {
  if (redirectsCache) return redirectsCache;

  const endpoints = ['/data/redirects.json', '/redirects.json'];

  for (const url of endpoints) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        redirectsCache = await res.json();
        return redirectsCache!;
      }
    } catch {
      // try next
    }
  }

  return [];
}

/**
 * Fetches all search chunks for client-side search via the authenticated API.
 */
export async function fetchSearchChunks(site: SiteId = DEFAULT_SITE.id, token?: string | null): Promise<SearchChunk[]> {
  const effectiveToken = token || getStoredAuthToken();
  if (chunksCache && chunksCache.token === effectiveToken && chunksCache.site === site) return chunksCache.data;

  const headers = authHeaders(effectiveToken);
  const res = await fetch(`/api/v1/search/chunks?site=${site}`, { headers });
  if (!res.ok) {
    return [];
  }

  const text = await res.text();
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      chunksCache = { token: effectiveToken, site, data: parsed };
      return parsed;
    }
  } catch {
    // Parse as newline-delimited JSON (JSONL)
  }
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const parsed = lines.map((line) => JSON.parse(line));
  chunksCache = { token: effectiveToken, site, data: parsed };
  return parsed;
}

/**
 * Searches documentation via backend hybrid search endpoint.
 */
export async function searchDocumentation(
  query: string,
  limit: number = 10,
  mode: 'hybrid' | 'vector' | 'keyword' = 'hybrid',
  site: SiteId = DEFAULT_SITE.id,
  token?: string | null,
  signal?: AbortSignal
): Promise<{ query: string; results: SearchChunk[]; total_hits: number; latency_ms: number; query_id?: string }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders(token),
  };

  const res = await fetch('/api/v1/search', {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, limit, mode, site }),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Search failed: ${res.statusText}`);
  }

  return await res.json();
}

/**
 * Submits feedback on AI answers (thumbs up / thumbs down).
 */
export async function submitAiFeedback(
  queryId: string,
  answerId: string,
  vote: 'up' | 'down',
  token?: string | null
): Promise<unknown> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders(token),
  };

  const res = await fetch('/api/v1/feedback', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      type: 'ai',
      query_id: queryId,
      answer_id: answerId,
      vote,
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to submit feedback: ${res.statusText}`);
  }

  return await res.json();
}
